/**
 * citationContextWalker — given a citation content control the writer
 * just clicked (or whose cursor they just entered), return the text the
 * citation lookup should treat as the "highlighted selection".
 *
 * # CONTRACT (revised 2026-06-08 — structural context)
 *  The selection is the **structural unit** of prose immediately before
 *  the clicked marker, classified by the marker's containing paragraph:
 *
 *  1. **List item** — paragraph carries a ``List*`` built-in style OR its
 *     text starts with a bullet/number prefix (``-``, ``*``, ``•``,
 *     ``1.``, ``2)``, …). Context = the list item's text from paragraph
 *     start to the marker, with the leading list marker stripped.
 *  2. **Small paragraph** — paragraph total length ≤ 800 chars and not a
 *     list item. Context = the full prefix from paragraph start to the
 *     marker.
 *  3. **Sentence-end (large paragraph)** — paragraph > 800 chars. Context
 *     = the prose between the previous sentence-ender (``.``, ``?``,
 *     ``!`` followed by whitespace) and the marker. Falls back to the
 *     full paragraph prefix when no boundary is found.
 *
 *  In all three cases, if a previous ``asthra-cite:*`` content control
 *  sits inside the chosen unit, the start clamps to that CC's end so
 *  the lookup never overlaps another citation's claim.
 *
 * # RANGE ARITHMETIC
 *  Office.js exposes ``Range.expandTo`` plus per-range ``Start``/``After``
 *  endpoints; we use them to build:
 *    - ``prefixRange = paragraphStart.expandTo(clickedCC.start)`` — the
 *      complete prose between paragraph start and the marker.
 *    - prior-CC clamp: scan the inner range's content controls, find the
 *      latest ``asthra-cite:*`` one via pairwise ``compareLocationWith``,
 *      replace ``paragraphStart`` with ``priorCC.end``.
 *
 *  Three syncs total (load → locate prior CC → read final text).
 */

import { CITATION_CC_TAG_PREFIX } from "./citationMarkerWriter";

/** Result of a context walk. ``text`` is the prose the citation lookup
 *  should treat as the writer's selection; ``empty`` means the walker
 *  could not assemble a usable selection (the bookmark was missing, the
 *  control was deleted between the click and our sync, etc.) and the
 *  caller should silently no-op. */
export interface CitationContextResult {
  text: string;
  empty: boolean;
}

/** Paragraph-size threshold separating "small paragraph" (whole-prefix
 *  context) from "large paragraph" (sentence-window context). 800 chars
 *  ≈ 130-150 words — a comfortable paragraph-as-one-claim ceiling.
 *  Lookup quality at the upstream Asthra side starts to degrade with
 *  noisier inputs past this point. */
const SMALL_PARA_THRESHOLD = 800;

/** Built-in Word styles that indicate a list-item paragraph. Markdown
 *  list rendering in the variant-c renderer applies one of these via
 *  ``styleBuiltIn`` so the detection lands here even when the writer
 *  hasn't typed a literal ``-`` prefix. */
const LIST_STYLE_RE = /list(bullet|number|paragraph)/i;

/** Visible bullet / numeric prefix the writer (or markdown renderer) may
 *  leave at the start of the paragraph text. Stripped from the context
 *  so the citation lookup doesn't waste its first hit on the bullet. */
const LIST_BULLET_RE = /^\s*([-*•]|\d+[.)])\s+/;

const EMPTY: CitationContextResult = { text: "", empty: true };

function isOfficeReady(): boolean {
  return (
    typeof Word !== "undefined" &&
    typeof Word.run === "function" &&
    typeof Office !== "undefined" &&
    Office.context !== undefined
  );
}

/** Pure helper: find the start offset of the "current sentence" within
 *  ``text`` — i.e. the position just after the most recent sentence-
 *  ender (``.`` / ``?`` / ``!`` followed by whitespace) that has at
 *  least one character of content after it. Returns 0 when no usable
 *  boundary exists (so the caller falls back to the full prefix).
 *
 *  Exported for unit-style testability in node — the Office.js code
 *  path consumes the same result. */
export function splitToSentenceBoundary(text: string): number {
  // Walk backward looking for ``[.?!]`` followed by whitespace. The
  // ``i+1`` guard means the very last character can't itself satisfy
  // the boundary (a trailing ``.`` would otherwise return text.length).
  for (let i = text.length - 2; i >= 0; i--) {
    if (!/[.?!]/.test(text[i])) continue;
    if (!/\s/.test(text[i + 1])) continue;
    // Skip past the whitespace run to land at the first content char
    // of the next (= current) sentence.
    let j = i + 1;
    while (j < text.length && /\s/.test(text[j])) j++;
    if (j < text.length) return j;
    // Boundary points past the end of text — no content in the
    // "current" sentence after it. Keep scanning for an earlier
    // boundary that does have content.
  }
  return 0;
}

/** Pure helper: strip the leading list-marker (``- ``, ``• ``, ``1. ``,
 *  ``2) ``) and surrounding whitespace from a list-item context. Used
 *  only in the list-item code path; safe-no-op when no marker matches. */
export function stripListPrefix(text: string): string {
  return text.replace(LIST_BULLET_RE, "").trim();
}

/** Extract the prose context that should drive the citation lookup for
 *  the marker the writer just clicked / entered with their cursor. */
export async function extractContextForCitation(params: {
  sectionNumber: string;
  controlId: number;
}): Promise<CitationContextResult> {
  if (!isOfficeReady()) return EMPTY;
  const { sectionNumber, controlId } = params;
  if (!sectionNumber || typeof controlId !== "number") return EMPTY;

  try {
    return await Word.run(async (context) => {
      const clickedCC = context.document.contentControls.getByIdOrNullObject(controlId);
      clickedCC.load("isNullObject,tag");
      await context.sync();
      if (clickedCC.isNullObject) return EMPTY;

      // The clicked CC's range and its leading boundary. ``Start`` is a
      // zero-width range right before the ``[`` character.
      const clickedRange = clickedCC.getRange("Whole");
      const clickedStart = clickedRange.getRange("Start");

      // The paragraph containing the marker. ``Range.paragraphs`` returns
      // every paragraph the range overlaps; for an inline CC that's
      // wholly within one paragraph this collection has exactly one
      // item. ``getFirst`` is sufficient.
      const paragraph = clickedRange.paragraphs.getFirst();
      paragraph.load("text,style,styleBuiltIn");
      const paraStart = paragraph.getRange("Start");

      // Build the paragraph-prefix range from paragraph start up to the
      // marker. ``expandTo`` requires the receiver come before the
      // argument in document order; this is by construction.
      let prefixRange: Word.Range;
      try {
        prefixRange = paraStart.expandTo(clickedStart);
      } catch (err) {
        console.warn(
          "[citationContextWalker] paragraph→clicked expandTo failed:",
          err,
        );
        return EMPTY;
      }

      // Find any previous ``asthra-cite:*`` CC inside the prefix — we
      // clamp the context start to its end so two adjacent citations
      // don't share text. ``Range.contentControls`` is limited to CCs
      // wholly inside the range.
      const innerCCs = prefixRange.contentControls;
      innerCCs.load("items/id,items/tag");
      await context.sync();

      const candidates: Word.ContentControl[] = [];
      for (const cc of innerCCs.items) {
        if (cc.id === controlId) continue;
        if (!cc.tag || !cc.tag.startsWith(CITATION_CC_TAG_PREFIX)) continue;
        candidates.push(cc);
      }

      let clampStart: Word.Range = paraStart;
      if (candidates.length > 0) {
        // Pick the candidate whose end is latest in document order. We
        // queue all pairwise ``compareLocationWith`` then read after one
        // sync (avoids N round-trips for N candidates).
        const candEnds = candidates.map((c) => c.getRange("After"));
        const cmps: OfficeExtension.ClientResult<Word.LocationRelation>[][] = [];
        for (let i = 0; i < candEnds.length; i++) {
          const row: OfficeExtension.ClientResult<Word.LocationRelation>[] = [];
          for (let j = 0; j < candEnds.length; j++) {
            row.push(candEnds[i].compareLocationWith(candEnds[j]));
          }
          cmps.push(row);
        }
        await context.sync();

        // Candidate i is "latest" iff every other j is Before i (rel ==
        // After) or Equal. ``Adjacent*`` variants don't occur between
        // distinct CC end-ranges in practice.
        let bestIdx = -1;
        for (let i = 0; i < candidates.length; i++) {
          let isLatest = true;
          for (let j = 0; j < candidates.length; j++) {
            if (i === j) continue;
            const rel = cmps[i][j].value;
            if (rel !== "After" && rel !== "Equal") {
              isLatest = false;
              break;
            }
          }
          if (isLatest) {
            bestIdx = i;
            break;
          }
        }
        if (bestIdx >= 0) clampStart = candEnds[bestIdx];
      }

      // Re-build the final prefix range using the clamped start. If no
      // prior CC was found, this is the same as the paragraph-start
      // prefix; otherwise it begins right after the previous marker.
      let finalRange: Word.Range;
      try {
        finalRange = clampStart.expandTo(clickedStart);
      } catch (err) {
        console.warn(
          "[citationContextWalker] clamp→clicked expandTo failed:",
          err,
        );
        return EMPTY;
      }
      finalRange.load("text");
      await context.sync();

      const rawPrefix = (finalRange.text ?? "");
      const trimmed = rawPrefix.trim();
      if (!trimmed) return EMPTY;

      // Classify the paragraph and pick the structural slice.
      const builtIn = (paragraph.styleBuiltIn || "").toLowerCase();
      const display = (paragraph.style || "").toLowerCase();
      const paraText = paragraph.text || "";
      const isListItem =
        LIST_STYLE_RE.test(builtIn) ||
        LIST_STYLE_RE.test(display) ||
        LIST_BULLET_RE.test(paraText);

      let contextText: string;
      if (isListItem) {
        contextText = stripListPrefix(rawPrefix);
      } else if (paraText.length <= SMALL_PARA_THRESHOLD) {
        contextText = trimmed;
      } else {
        const sentStart = splitToSentenceBoundary(rawPrefix);
        contextText = rawPrefix.slice(sentStart).trim();
        // Defensive: if sentence slice came out empty (e.g. text was
        // pure whitespace after the boundary), fall back to the full
        // prefix rather than returning nothing.
        if (!contextText) contextText = trimmed;
      }

      if (!contextText) return EMPTY;
      return { text: contextText, empty: false };
    });
  } catch (err) {
    console.warn(
      "[citationContextWalker] extract failed (non-fatal):",
      err,
    );
    return EMPTY;
  }
}
